import { useEffect, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { FlexGrid as Grid, Column, Row } from '@carbon/react';
import { resourcesAtom } from 'atoms/root';
import { DocumentAttributesDetailView } from 'components/document-attributes-detail-view';
import {
  createBoundingBoxAtom,
  Map,
  PolygonArea,
  useCalculateMapDimensions,
  useMapResize,
} from 'components/map';
import { Section } from 'components/section';
import { WellMapLayer } from 'components/well-map-layer';
import type { DocumentResponse } from 'types/api-responses';
import {
  afeWellLayersConfigAtom,
  areasMapRefAtom,
  getLocationGeometries,
  getReferenceLocationIds,
  mapConfigAtom,
} from './atoms';
import { useAfeWellbores } from './hooks';
import { getAfeWellLayers, getWellboreBounds } from './utils';
import style from './overview-map-section.module.scss';

const SINGLE_WELL_ZOOM = 12;

/**
 * The map of a document's locations and, for an AFE, its matched wells.
 * @param {{ document: DocumentResponse }} props - The document to map
 * @return {JSX.Element} The map
 */
function AreasMap({ document }: { document: DocumentResponse }) {
  const containerRef = useRef(null);
  const resources = useAtomValue(resourcesAtom);

  const referenceLocationIds = useMemo(
    () => getReferenceLocationIds(document),
    [document]
  );
  const geometries = useMemo(() => getLocationGeometries(document), [document]);

  const boundingBoxAtom = useMemo(
    () =>
      createBoundingBoxAtom(
        referenceLocationIds,
        resources?.boundingBoxes.href
      ),
    [referenceLocationIds, resources?.boundingBoxes.href]
  );
  const boundingBox = useAtomValue(boundingBoxAtom);

  // AFE documents only: the wells matched to the AFE's targets. Empty (and
  // request-free) for every other document type, so their map is unchanged.
  const { wellbores: afeWellbores, isSettled: afeWellboresSettled } =
    useAfeWellbores(document);
  const afeWellLayers = useMemo(
    () => getAfeWellLayers(afeWellbores),
    [afeWellbores]
  );
  const wellBounds = useMemo(
    () => getWellboreBounds(afeWellbores),
    [afeWellbores]
  );

  // Base viewport from the document location; unchanged for non-AFE docs.
  const { latitude, longitude, zoom } = useCalculateMapDimensions({
    points: boundingBox.length ? boundingBox : undefined,
    containerRef,
  });

  // Once every AFE well lookup settles, zoom the map to the wells imperatively
  // (the declarative viewport props don't reliably re-fit an already-mounted
  // map). Fitting per lookup would jump the camera as each late well arrived.
  // fitBounds centers on a single surface hole at maxZoom, so no special case.
  const areasMapRef = useAtomValue(areasMapRefAtom);
  // A side panel pushing the page resizes this container, not the window.
  useMapResize(containerRef, areasMapRef);
  useEffect(() => {
    if (!areasMapRef || !wellBounds || !afeWellboresSettled) return;
    areasMapRef
      .getMap()
      ?.fitBounds(wellBounds, { padding: 60, maxZoom: SINGLE_WELL_ZOOM });
  }, [areasMapRef, wellBounds, afeWellboresSettled]);

  const hoveredReferenceLocations = useMemo(
    () =>
      geometries.map((location) => ({
        id: location.id,
        name: location.name,
        quarterCalls: location.quarterCalls,
      })),
    [geometries]
  );

  return (
    <div ref={containerRef}>
      <Map
        className={style.map}
        longitude={longitude}
        latitude={latitude}
        zoom={zoom}
        showControls
        showMapFilters
        mapConfigAtom={mapConfigAtom}
        refAtom={areasMapRefAtom}
        geographies={hoveredReferenceLocations}
      >
        {geometries.map((el) => (
          <PolygonArea geometry={el.geometry} id={el.id} key={el.id} />
        ))}
        {afeWellLayers.map(({ id, wellbore }) => (
          <WellMapLayer
            key={id}
            id={id}
            wellbore={wellbore}
            mapConfigAtom={afeWellLayersConfigAtom}
          />
        ))}
      </Map>
    </div>
  );
}

/**
 * The document's details beside a map of its locations (and, for an AFE, its
 * matched wells).
 * @param {{ document: DocumentResponse }} props - The document to show
 * @return {JSX.Element} The section
 */
export function OverviewMapSection({
  document,
}: {
  document: DocumentResponse;
}) {
  return (
    <Section header="" className={style.container}>
      <Grid fullWidth condensed className="bx--no-gutter">
        <Row className={style.row}>
          <Column sm={4} lg={4} className={style.detail}>
            <DocumentAttributesDetailView document={document} />
          </Column>
          <Column sm={12} lg={12} className={style.colMap}>
            <AreasMap document={document} />
          </Column>
        </Row>
      </Grid>
    </Section>
  );
}
